/**
 * Types for the cross-provider admin subscriptions console (#1701).
 *
 * Mirrors `AdminSubscriptionInfo` in
 * `functions/src/api/routes/adminSubscriptions.ts`, which serialises the
 * observational `subscriptionMirrors` collection. That collection records what
 * a PROVIDER says about billing. It never drives access, so nothing here should
 * be read, or rendered, as a statement about what a user can do.
 */

/**
 * The closed cross-provider state vocabulary. The only axis on which one filter
 * means the same thing for every provider — raw `status` is the provider's own
 * word and is not comparable across them.
 *
 * Kept in the same order as the backend union so the two are diffable by eye.
 */
export type NormalizedSubscriptionState =
  | 'active'
  | 'grace'
  | 'on_hold'
  | 'paused'
  | 'pending'
  | 'ended'
  | 'suppressed';

export const NORMALIZED_STATE_LABELS: Record<
  NormalizedSubscriptionState,
  string
> = {
  active: 'Active',
  grace: 'Grace period',
  on_hold: 'On hold',
  paused: 'Paused',
  pending: 'Pending',
  ended: 'Ended',
  // Not "Echo (suppressed)": both words were ours, not the reader's. An admin
  // cannot infer either, and the badge is the first thing they see on the row.
  suppressed: 'Duplicate (ignored)',
};

/**
 * Providers the API's `provider` enum accepts today. Widening the mirror is not
 * enough to add one: the query parameter is an `enum` in `openapi.yaml` under
 * `validateRequests`, so an undeclared value is a 400 before the handler runs.
 */
export const SUBSCRIPTION_PROVIDERS = ['stripe', 'supapass'] as const;
export type SubscriptionProvider = (typeof SUBSCRIPTION_PROVIDERS)[number];

export interface AdminSubscriptionInfo {
  id: string;
  provider: string;
  providerSubscriptionId: string;
  providerCustomerId: string | null;
  /** The provider's own word, verbatim. Per-provider vocabulary. */
  status: string;
  normalizedStatus: NormalizedSubscriptionState;
  productIds: string[];
  /** Resolved Firebase uid, or '' when no Heapster user is linked. */
  uid: string;
  email: string | null;
  currentPeriodEnd: string | null;
  /**
   * TRI-STATE, both of them. `null` means the provider does not expose renewal
   * intent — always the case for supapass — which is a different fact from
   * `false`. Rendering `null` as "No" would show a live subscription as
   * expiring.
   */
  cancelAtPeriodEnd: boolean | null;
  willRenew: boolean | null;
  lastEventId: string;
  lastEventAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface AdminSubscriptionsPage {
  subscriptions: AdminSubscriptionInfo[];
  hasMore: boolean;
  nextCursor?: string;
}

/**
 * The one filter axis a request may carry, beyond `provider`.
 *
 * The endpoint accepts at most ONE of status / normalizedStatus / productId /
 * a user filter, and `provider` may accompany any of them except `productId`.
 * Modelling that as a single axis is what stops the UI from constructing a
 * combination the API answers 400.
 */
export type SubscriptionFilterAxis =
  | 'none'
  | 'normalizedStatus'
  | 'status'
  | 'productId'
  | 'user';

export interface SubscriptionQueryParams {
  provider?: string;
  normalizedStatus?: string;
  status?: string;
  productId?: string;
  uid?: string;
  orphan?: boolean;
  limit?: number;
  startAfter?: string;
}
